import React, { useCallback, useMemo, useState } from "react";
import { FrameContextType, FrameContext } from "./FrameContext";

export const FrameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
    const [hoveredAnnotationId, setHoveredAnnotationId] = useState<string | null>(null);
    const [shouldShowAnnotationInput, setShouldShowAnnotationInput] = useState<boolean>(false);
    const [orderedTimelineFramePreviewUrls, setOrderedTimelineFramePreviewUrls] = useState<{
        [fileUuid: string]: { [numFileFrames: number]: string[] };
    }>({});

    // Consumers call this, but implementation lives in the player component.
    const requestSeekToTime = useCallback((time: number) => {
        // Intentionally just a warning – concrete players should override this when mounted.
        console.warn(
            `requestSeekToTime called with ${time}s, but no player implementation is registered.`
        );
    }, []);

    // Ref to hold the cancel function from useAutoPredictions
    const cancelAutoPredictionsRef = React.useRef<(() => void) | null>(null);
    const cancelAutoPredictions = useCallback(() => {
        console.log("[FRAME] cancelAutoPredictions called", {
            hasRef: !!cancelAutoPredictionsRef.current
        });
        if (cancelAutoPredictionsRef.current) {
            cancelAutoPredictionsRef.current();
        } else {
            console.warn("[FRAME] cancelAutoPredictions ref is null - cannot cancel!");
        }
    }, []);

    // get the text prompt proposals for the current frame
    const [currentFrameUploadTextPromptProposals, setCurrentFrameUploadTextPromptProposals] =
        useState<string[] | undefined>(undefined);

    const stableSetters = useMemo<
        Pick<
            FrameContextType,
            | "setSelectedAnnotationId"
            | "setHoveredAnnotationId"
            | "setShouldShowAnnotationInput"
            | "setOrderedTimelineFramePreviewUrls"
            | "setCurrentFrameUploadTextPromptProposals"
            | "requestSeekToTime"
            | "cancelAutoPredictions"
        >
    >(
        () => ({
            setCurrentFrameUploadTextPromptProposals,
            setSelectedAnnotationId,
            setHoveredAnnotationId,
            setShouldShowAnnotationInput,
            setOrderedTimelineFramePreviewUrls,
            requestSeekToTime,
            cancelAutoPredictions
        }),
        [requestSeekToTime, cancelAutoPredictions]
    );

    const contextValue = useMemo<FrameContextType>(
        () => ({
            currentFrameUploadTextPromptProposals,
            selectedAnnotationId,
            hoveredAnnotationId,
            shouldShowAnnotationInput,
            orderedTimelineFramePreviewUrls,
            cancelAutoPredictionsRef, // Export the ref
            ...stableSetters
        }),
        [
            currentFrameUploadTextPromptProposals,
            selectedAnnotationId,
            hoveredAnnotationId,
            shouldShowAnnotationInput,
            orderedTimelineFramePreviewUrls,
            stableSetters
        ]
    );

    return <FrameContext.Provider value={contextValue}>{children}</FrameContext.Provider>;
};
